import { Permissions } from '#root/shared/enums/permission.enum'
import {
  RouteEnum,
  ROUTEPERMISSIONS,
} from '#root/shared/enums/route-permissions'
import { hideItemNavigation } from '#utils/hide-item-navigation/hide-item-navigation'
import { FuseNavigationItem } from '@fuse/components/navigation'

export default function NavBarByPermission(
  userPermissions: Permissions[],
): FuseNavigationItem[] {
  const hideItemByPermissions = (item: FuseNavigationItem): boolean => {
    if (item.children?.length) {
      return item.children.every((child) => hideItemByPermissions(child));
    }

    const mapItem = getMapItem(item);

    if (!mapItem) return false

    return hideItemNavigation({
      permissions: mapItem.permissions ?? [],
      userPermissions: userPermissions,
      mode: mapItem.mode ?? 'any',
      featureFlag: mapItem.featureFlag ?? null,
      hideWhenFeatureEnabled: mapItem.hideWhenFeatureEnabled ?? null,
    })
  }

  const getMapItem = (item: FuseNavigationItem) => {
    if (item.link in ROUTEPERMISSIONS) {
      return ROUTEPERMISSIONS[item.link]
    }

    if (item.id in ROUTEPERMISSIONS) {
      return ROUTEPERMISSIONS[item.id]
    }
  }

  const nav: FuseNavigationItem[] = [
    {
      id: 'crm',
      title: 'CRM',
      subtitle: 'Interactua con los clientes',
      type: 'group',
      icon: 'heroicons_outline:home',
      children: [
        {
          id: 'dashboard-v2',
          title:
            localStorage.getItem('rol') === 'SalesAdvisor'
              ? 'Inicio'
              : 'Dashboard',
          type: 'basic',
          icon: 'heroicons_outline:chart-pie',
          link: '/dashboard',
        },
        {
          id: 'usersProspects',
          title: 'Leads',
          type: 'basic',
          icon: 'heroicons_outline:user',
          link: '/crm/prospects',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "crm.opportunity",
          title: "Oportunidades",
          type: 'basic',
          icon: "heroicons_outline:shopping-cart",
          link: "/crm/opportunity",
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'crm.clientes',
          title: 'Clientes',
          type: 'basic',
          icon: 'heroicons_outline:users',
          link: '/crm/customers',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'activities',
          title: 'Calendario de Actividades',
          type: 'basic',
          icon: 'heroicons_outline:calendar',
          link: '/crm/activities',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "crm.evaluations.group",
          title: "Evaluaciones",
          type: "collapsable",
          icon: "heroicons_outline:shield-check",
          children: [
            {
              id: "crm.evaluations",
              title: "Evaluaciones",
              type: "basic",
              link: RouteEnum.CRM_EVALUATIONS,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: "globalConfig.pepTypes",
              title: "Tipos de PEP",
              type: "basic",
              link: RouteEnum.GLOBAL_CONFIG_PEP_TYPES,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: "globalConfig.evaluationReasons",
              title: "Motivos de Evaluación",
              type: "basic",
              link: RouteEnum.GLOBAL_CONFIG_EVALUATION_REASONS,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'crm.payment_order',
          title: 'Planes de pagos',
          type: 'basic',
          icon: 'heroicons_outline:receipt-percent',
          link: '/crm/payment-orders',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "crm.payment_order_approvals",
          title: "Aprobación de planes de pagos",
          type: "basic",
          icon: "mat_outline:pending_actions",
          link: "/crm/payment-orders-approvals",
          // hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'payments-request',
          title: 'Confirmación de pagos',
          type: 'basic',
          icon: 'heroicons_outline:wallet',
          link: '/crm/payments-request',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'crm.pending_invoices',
          title: 'Pendientes de facturar',
          type: 'basic',
          icon: 'heroicons_outline:document-text',
          link: '/crm/pending-invoices',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "crm.payments-of-week",
          title: "Ingresos del mes",
          type: "basic",
          icon: "heroicons_outline:cash",
          link: "/crm/payments-of-week",
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "crm.unidentified_payer_payments",
          title: "Pagos sin pagador identificado",
          type: "basic",
          icon: "heroicons_outline:banknotes",
          link: "/crm/unidentified-payer-payments",
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'change-of-product',
          title: 'Cambios de inmueble',
          type: 'basic',
          icon: 'heroicons_outline:switch-horizontal',
          link: '/crm/change-of-product',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'cession-of-right',
          title: 'Cesiones de derecho',
          type: 'basic',
          icon: 'heroicons_outline:list-bullet',
          link: '/crm/cession-of-right',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'withdrawal-requests',
          title: 'Solicitudes de retiros',
          type: 'basic',
          icon: 'heroicons_outline:x-circle',
          link: '/crm/withdrawal-requests',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'customersWallet',
          title: 'Asesores de venta',
          type: 'basic',
          icon: 'heroicons_outline:briefcase',
          link: '/crm/salesAdvisors',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'paymentCommissions',
          title: 'Pago de comisiones',
          type: 'basic',
          icon: 'heroicons_outline:document-check',
          link: '/crm/commission-payments',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'crm.goal-commissions.group',
          title: 'Comisiones por cumplimiento',
          type: 'collapsable',
          icon: 'heroicons_outline:presentation-chart-line',
          children: [
            {
              id: 'crm.goal-commissions.mine',
              title: 'Mis comisiones',
              type: 'basic',
              link: RouteEnum.CRM_GOAL_COMMISSIONS_MINE,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'crm.goal-commissions.manage',
              title: 'Gestión',
              type: 'basic',
              link: RouteEnum.CRM_GOAL_COMMISSIONS_MANAGE,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'crm.goal-commissions.settings',
              title: 'Configuración',
              type: 'basic',
              link: RouteEnum.CRM_GOAL_COMMISSIONS_SETTINGS,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'reports',
          title: 'Reportes',
          type: 'collapsable',
          icon: 'heroicons_outline:folder-open',
          link: '/crm/report',
          children: [
            {
              id: 'report.old_balances',
              title: 'Antiguedad de saldos',
              type: 'basic',
              link: '/crm/report/old-balances',
              hidden: (item) => hideItemByPermissions(item),
            },
            // {
            //   id: "report.advisor_goal",
            //   title: "Metas de Asesores",
            //   type: "basic",
            //   link: "/crm/report/advisor-goal",
            //   hidden: (item) => hideItemByPermissions(item),
            // },
            // {
            //   id: "report.advisor_activities",
            //   title: "Actividades de asesores",
            //   type: "basic",
            //   link: "/crm/report/advisor-activities",
            //   hidden: (item) => hideItemByPermissions(item),
            // },
            {
              id: 'report.customer_payment_status',
              title: 'Estado de cuenta de clientes',
              type: 'basic',
              link: '/crm/report/customers-payment-status',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.customer_payment_group',
              title: 'Estado de cuenta agrupado',
              type: 'basic',
              link: '/crm/report/customers-payment-group',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.pending_with_drawal',
              title: 'Devoluciones pendientes',
              type: 'basic',
              link: '/crm/report/pending-with-drawal',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.withdrawals_report',
              title: 'Reporte de retiros',
              type: 'basic',
              link: '/crm/report/withdrawals-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.withdrawals_report',
              title: 'Reporte de retiros',
              type: 'basic',
              link: '/crm/report/withdrawals-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.penalty_withdrawal_report',
              title: 'Penalidades por retiro',
              type: 'basic',
              link: '/crm/report/penalty-withdrawal-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.general_leads_report',
              title: 'Leads generales',
              type: 'basic',
              link: '/crm/report/general-leads-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.loss_reason_report',
              title: 'Razones de pérdida',
              type: 'basic',
              link: '/crm/report/loss-reason-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.internal_external_sales_report',
              title: 'Ventas internas vs externas',
              type: 'basic',
              link: '/crm/report/internal-external-sales-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.customers_advisor',
              title: 'Clientes por asesores',
              type: 'basic',
              link: '/crm/report/customers-advisor',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.advisor_payment_status',
              title: 'Estado de Cuenta de Asesores',
              type: 'basic',
              link: '/crm/report/advisor-payment-status',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.top_sold',
              title: 'Inmuebles vendidos',
              type: 'basic',
              link: '/crm/report/top-sold',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.top_quote',
              title: 'Inmuebles más cotizados',
              type: 'basic',
              link: '/crm/report/top-quote',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.quoted_products_client',
              title: 'Inmuebles cotizados por cliente',
              type: 'basic',
              link: '/crm/report/quoted-products-client',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.opportunity_origins',
              title: 'Orígenes de oportunidades',
              type: 'basic',
              link: '/crm/report/opportunity-origins',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.clients_by_quoted_product',
              title: 'Clientes por productos cotizados',
              type: 'basic',
              link: '/crm/report/clients-by-quoted-product',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.master',
              title: 'Oportunidades',
              type: 'basic',
              link: '/crm/report/master',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.master-leads',
              title: 'Leads',
              type: 'basic',
              link: '/crm/report/master-leads',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.master-clients',
              title: 'Clientes',
              type: 'basic',
              link: '/crm/report/master-client',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.master-products',
              title: 'Inmuebles',
              type: 'basic',
              link: '/crm/report/master-product',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.available-products',
              title: 'Inmuebles disponibles',
              type: 'basic',
              link: '/crm/report/available-products',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.payments-history',
              title: 'Historial de pagos',
              type: 'basic',
              link: '/crm/report/payments-history',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.payments-reserve-history',
              title: 'Pagos de reserva',
              type: 'basic',
              link: '/crm/report/payments-reserve-history',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.customer-info-report',
              title: 'Información de clientes activos',
              type: 'basic',
              link: '/crm/report/customer-info-report',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: "report.accounts-receivable",
              title: "Cuentas por cobrar",
              type: "basic",
              link: "/crm/report/accounts-receivable",
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.documentation-compliance',
              title: 'Cumplimiento de documentación',
              type: 'basic',
              link: '/crm/report/documentation-compliance',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.paid-commission-per-month',
              title: 'Pagos de comisiones',
              type: 'basic',
              link: '/crm/report/paid-commission-per-month',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.pending-paid-commissions',
              title: 'Pagos de comisiones pendientes',
              type: 'basic',
              link: '/crm/report/pending-paid-commissions',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.commission-per-project',
              title: 'Comisiones por proyecto',
              type: 'basic',
              link: '/crm/report/commissions-per-project',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.commissions-available-for-payment',
              title: 'Comisiones disponibles para pago',
              type: 'basic',
              link: '/crm/report/commissions-available-for-payment',
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'report.opportunity-step-transition-time',
              title: 'Tiempo de transición entre pasos',
              type: 'basic',
              link: '/crm/report/opportunity-step-transition-time',
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
      ],
      hidden: (item) => hideItemByPermissions(item),
    },
    {
      id: 'inventory',
      title: 'Inventario',
      subtitle: 'Administra los inmuebles y proveedores',
      type: 'group',
      icon: 'heroicons_outline:home',
      children: [
        {
          id: 'inventory.products',
          title: 'Inmuebles',
          type: 'basic',
          icon: 'heroicons_outline:shopping-bag',
          link: '/inventory/products',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'inventory.categoryProducts.category',
          title: 'Categorías',
          type: 'basic',
          icon: 'heroicons_outline:tag',
          link: '/inventory/categoryProducts/categories',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'inventory.categoryProducts.subcategory',
          title: 'Sub categorías',
          type: 'basic',
          icon: 'heroicons_outline:tag',
          link: '/inventory/categoryProducts/subcategories',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'inventory.characteristic',
          title: 'Caracteristicas',
          type: 'basic',
          icon: 'heroicons_outline:search-circle',
          link: '/inventory/characteristics',
          hidden: (item) => hideItemByPermissions(item),
        },
      ],
      hidden: (item) => hideItemByPermissions(item),
    },
    {
      id: 'ecommerce',
      title: 'Ecommerce',
      subtitle: 'Gestiona los datos del ecommerce',
      type: 'group',
      icon: 'heroicons_outline:home',
      children: [
        {
          id: 'ecommerce.businessData',
          title: 'Datos de la empresa',
          type: 'basic',
          icon: 'heroicons_outline:database',
          link: '/ecommerce/businessData',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'ecommerce.project',
          title: 'Proyectos',
          type: 'basic',
          icon: 'heroicons_outline:office-building',
          link: '/ecommerce/project',
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.commissions',
          title: 'Comisiones',
          type: 'basic',
          icon: 'heroicons_outline:wallet',
          link: '/ecommerce/commissions',
          hidden: (item) => hideItemByPermissions(item),
        },
      ],
      hidden: (item) => hideItemByPermissions(item),
    },
    {
      id: 'globalConfig',
      title: 'Configuraciones Globales',
      subtitle: 'Configura datos, tipos de datos y estados',
      type: 'group',
      icon: 'heroicons_outline:home',
      children: [
        {
          id: 'crm.adCampaign',
          title: 'Campañas Publicitarias',
          type: 'basic',
          icon: 'heroicons_outline:megaphone',
          link: RouteEnum.GLOBAL_CONFIG_AD_CAMPAIGN,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'ecommerce.bank',
          title: 'Bancos',
          type: 'basic',
          icon: 'mat_outline:comment_bank',
          link: RouteEnum.GLOBAL_CONFIG_BANKS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'ecommerce.payment_accounts',
          title: 'Cuentas de pago',
          type: 'basic',
          icon: 'heroicons_outline:banknotes',
          link: RouteEnum.GLOBAL_CONFIG_PAYMENT_ACCOUNTS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.exchangeRates',
          title: 'Tasas de Cambio',
          type: 'basic',
          icon: 'heroicons_outline:currency-dollar',
          link: RouteEnum.GLOBAL_CONFIG_EXCHANGE_RATES,
          hidden: (item) => hideItemByPermissions(item),
        },
        // {
        //   id: "globalConfig.documentType",
        //   title: "Tipos de Documentos",
        //   type: "basic",
        //   icon: "heroicons_outline:document",
        //   link: RouteEnum.GLOBAL_CONFIG_DOCUMENT_TYPE,
        //   hidden: (item) => hideItemByPermissions(item),
        // },
        {
          id: 'globalConfig.documents',
          title: 'Documentos',
          type: 'collapsable',
          icon: 'heroicons_outline:document',
          link: RouteEnum.GLOBAL_CONFIG_DOCUMENT,
          children: [
            {
              id: 'globalConfig.activities.list',
              title: 'Documentos',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_DOCUMENT_LIST,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.activities.activityTypes',
              title: 'Tipos de Documentos',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_DOCUMENT_TYPE,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.typeOrigins',
          title: 'Tipos de Origen',
          type: 'basic',
          icon: 'mat_outline:account_tree',
          link: RouteEnum.GLOBAL_CONFIG_ORIGIN_TYPE,
          hidden: (item) => hideItemByPermissions(item),
        },
        // {
        //   id: "globalConfig.typeActivity",
        //   title: "Tipos de Actividades",
        //   type: "basic",
        //   icon: "heroicons_outline:calendar",
        //   link: RouteEnum.GLOBAL_CONFIG_ACTIVITY,
        //   hidden: (item) => hideItemByPermissions(item),
        // },
        {
          id: 'globalConfig.activities',
          title: 'Actividades',
          type: 'collapsable',
          icon: 'heroicons_outline:calendar',
          link: RouteEnum.GLOBAL_CONFIG_ACTIVITY,
          children: [
            {
              id: 'globalConfig.activities.list',
              title: 'Actividades',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_ACTIVITY_LIST,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.activities.activityTypes',
              title: 'Tipos de Actividades',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_ACTIVITY_TYPE,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.type-commissions',
          title: 'Tipos de comisiones',
          type: 'basic',
          icon: 'heroicons_outline:wallet',
          link: RouteEnum.GLOBAL_CONFIG_TYPE_COMMISSIONS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.measureUnit',
          title: 'Unidades de Medida',
          type: 'basic',
          icon: 'heroicons_outline:variable',
          link: RouteEnum.GLOBAL_CONFIG_MEASURE_UNIT,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.goalParameters',
          title: 'Parametros de Metas',
          type: 'collapsable',
          icon: 'heroicons_outline:chart-bar',
          link: RouteEnum.GLOBAL_CONFIG_GOAL_PARAMETERS,
          children: [
            {
              id: 'globalConfig.goalParameters.goals',
              title: 'Parámetros de metas',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_GOALS,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.goalParameters.metrics',
              title: 'Métricas',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_GOAL_METRICS,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.status',
          title: 'Estados',
          type: 'collapsable',
          icon: 'heroicons_outline:check-circle',
          link: RouteEnum.GLOBAL_CONFIG_STATUS,
          children: [
            {
              id: 'globalConfig.status.list',
              title: 'Estados',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_STATUS_LIST,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.status.typeStatus',
              title: 'Tipos de estados',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_STATUS_TYPES,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.prospectStatus',
              title: 'Estados de Leads',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_PROSPECT_STATUS,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.prospectStepStatus',
              title: 'Relación Paso-Estado Leads',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_PROSPECT_STEP_STATUS,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.locations',
          title: 'Locaciones',
          type: 'collapsable',
          icon: 'heroicons_outline:globe',
          link: RouteEnum.GLOBAL_CONFIG_LOCATIONS,
          children: [
            {
              id: 'globalConfig.locations.departments',
              title: 'Departamentos',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_LOCATIONS_DEPARTMENTS,
              hidden: (item) => hideItemByPermissions(item),
            },
            {
              id: 'globalConfig.locations.cities',
              title: 'Municipios',
              type: 'basic',
              link: RouteEnum.GLOBAL_CONFIG_LOCATIONS_CITIES,
              hidden: (item) => hideItemByPermissions(item),
            },
          ],
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.winReasons',
          title: 'Razones de ganancia',
          type: 'basic',
          icon: 'heroicons_outline:trending-up',
          link: RouteEnum.GLOBAL_CONFIG_WIN_REASONS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.lossReasons',
          title: 'Razones de perdida',
          type: 'basic',
          icon: 'heroicons_outline:trending-down',
          link: RouteEnum.GLOBAL_CONFIG_LOSS_REASONS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.lossReasonCategories',
          title: 'Categorías de razones de pérdida',
          type: 'basic',
          icon: 'heroicons_outline:tag',
          link: RouteEnum.GLOBAL_CONFIG_LOSS_REASON_CATEGORIES,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.discardReasons',
          title: 'Razones de descarte',
          type: 'basic',
          icon: 'heroicons_outline:x-circle',
          link: RouteEnum.GLOBAL_CONFIG_DISCARD_REASONS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "globalConfig.discountTypes",
          title: "Tipos de descuento",
          type: "basic",
          icon: "heroicons_outline:tag",
          link: RouteEnum.GLOBAL_CONFIG_DISCOUNT_TYPES,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: "globalConfig.paymentReasons",
          title: "Razones de modificación de pago",
          type: "basic",
          icon: "heroicons_outline:cash",
          link: RouteEnum.GLOBAL_CONFIG_PAYMENT_REASONS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.notifications',
          title: 'Notificaciones',
          type: 'basic',
          icon: 'mat_outline:message',
          link: RouteEnum.GLOBAL_CONFIG_NOTIFICATIONS,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.roles',
          title: 'Roles',
          type: 'basic',
          icon: 'heroicons_outline:user-group',
          link: RouteEnum.GLOBAL_CONFIG_ROLES,
          hidden: (item) => hideItemByPermissions(item),
        },
        {
          id: 'globalConfig.users',
          title: 'Usuarios',
          type: 'basic',
          icon: 'heroicons_outline:users',
          link: RouteEnum.GLOBAL_CONFIG_USERS,
          hidden: (item) => hideItemByPermissions(item),
        },
      ],
      hidden: (item) => hideItemByPermissions(item),
    },
  ]

  return nav
}
